'use client';

import { ElementRef, createContext, use } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { toggleVariants } from '@/components/ui/toggle';

const ToggleGroupContext = createContext<VariantProps<typeof toggleVariants>>({
  size: 'default',
  variant: 'default'
});

const ToggleGroup = ({
  ref,
  className,
  variant,
  size,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleVariants> & {
    ref?: React.RefObject<ElementRef<typeof ToggleGroupPrimitive.Root> | null>;
  }) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn('flex items-center justify-center gap-1', className)}
    {...props}
  >
    <ToggleGroupContext value={{ variant, size }}>
      {children}
    </ToggleGroupContext>
  </ToggleGroupPrimitive.Root>
);

ToggleGroup.displayName =
  ToggleGroupPrimitive.Root.displayName || 'ToggleGroup';

const ToggleGroupItem = ({
  ref,
  className,
  children,
  variant,
  size,
  ...props
}: ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleVariants> & {
    ref?: React.RefObject<ElementRef<typeof ToggleGroupPrimitive.Item> | null>;
  }) => {
  const context = use(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size
        }),
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
};

ToggleGroupItem.displayName =
  ToggleGroupPrimitive.Item.displayName || 'ToggleGroupItem';

export { ToggleGroup, ToggleGroupItem };
